import { useGradeScale } from "@hooks/useGradeScale"
import { cn } from "@lib/cn"
import { formatGrade, formatPercent } from "@lib/intl"
import {
  AlertTriangle,
  GraduationCap,
  Send,
  TrendingDown,
  TrendingUp,
  Users,
  UsersRound,
  UserX,
} from "lucide-react"
import { memo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import type { DirectionKpis } from "../types"

import { gradeTier, gradeTierTextClass } from "@/constants/gradeTier"
import type { GradeScale } from "@/types"

interface Props {
  kpis: DirectionKpis
}

interface KpiCardProps {
  icon: ReactNode
  label: string
  value: ReactNode
  delta: number
  // Some metrics are "lower is better" (at_risk, inactive). For those a
  // negative delta is a *win* and renders green; positive renders red.
  invertColor?: boolean
  deltaSuffix?: string
  deltaFormatter?: (n: number) => string
  valueClass?: string
}

// Institution-wide average → "group" tier, scale-aware.
const gradeColor = (avg: number, scale: GradeScale) =>
  gradeTierTextClass(gradeTier(avg, scale, "group"))
const deliveryColor = (rate: number) => gradeTierTextClass(gradeTier(rate, "0-100", "group"))

function DeltaChip({
  delta,
  invertColor,
  suffix,
  formatter,
}: {
  delta: number
  invertColor?: boolean
  suffix?: string
  formatter?: (n: number) => string
}) {
  const goodDirection = invertColor ? delta < 0 : delta > 0
  const isFlat = delta === 0
  const tone = isFlat
    ? "bg-inset text-ink-subtle"
    : goodDirection
      ? "bg-success-soft text-success-ink"
      : "bg-danger-soft text-danger-ink"
  const Arrow = isFlat ? null : goodDirection ? TrendingUp : TrendingDown
  const formatted = formatter ? formatter(Math.abs(delta)) : Math.abs(delta).toString()
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : "±"
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums",
        tone,
      )}
    >
      {Arrow ? <Arrow className="h-3 w-3" aria-hidden="true" /> : null}
      <span>
        {sign}
        {formatted}
        {suffix ?? ""}
      </span>
    </span>
  )
}

function KpiCard({
  icon,
  label,
  value,
  delta,
  invertColor,
  deltaSuffix,
  deltaFormatter,
  valueClass,
}: KpiCardProps) {
  return (
    <div className="rounded-card border-border bg-surface flex flex-col items-start gap-1.5 border p-3">
      <span className="bg-inset text-ink-muted flex h-7 w-7 items-center justify-center rounded-md">
        {icon}
      </span>
      <span className="text-ink-subtle text-xs font-medium">{label}</span>
      <span className={cn("text-2xl font-semibold tabular-nums", valueClass)}>{value}</span>
      <DeltaChip
        delta={delta}
        invertColor={invertColor}
        suffix={deltaSuffix}
        formatter={deltaFormatter}
      />
    </div>
  )
}

export const PulsoStrip = memo(function PulsoStrip({ kpis }: Props) {
  const { t } = useTranslation("direction")
  const scale = useGradeScale()
  return (
    <section
      aria-label={t("pulso.section_label")}
      className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6"
    >
      <KpiCard
        icon={<UsersRound className="h-4 w-4" />}
        label={t("pulso.professors.label")}
        value={kpis.total_professors}
        delta={kpis.delta_total_professors}
      />
      <KpiCard
        icon={<Users className="h-4 w-4" />}
        label={t("pulso.students.label")}
        value={kpis.total_students}
        delta={kpis.delta_total_students}
      />
      <KpiCard
        icon={<GraduationCap className="h-4 w-4" />}
        label={t("pulso.avg_grade.label")}
        value={formatGrade(kpis.avg_grade, scale)}
        valueClass={gradeColor(kpis.avg_grade, scale)}
        delta={kpis.delta_avg_grade}
        deltaFormatter={(n) => formatGrade(n, scale)}
      />
      <KpiCard
        icon={<Send className="h-4 w-4" />}
        label={t("pulso.delivery_rate.label")}
        value={formatPercent(kpis.delivery_rate)}
        valueClass={deliveryColor(kpis.delivery_rate)}
        delta={kpis.delta_delivery_rate}
        deltaSuffix=" pp"
      />
      <KpiCard
        icon={<AlertTriangle className="h-4 w-4" />}
        label={t("pulso.at_risk.label")}
        value={kpis.at_risk_count}
        valueClass={kpis.at_risk_count > 0 ? "text-danger-ink" : undefined}
        delta={kpis.delta_at_risk_count}
        invertColor
      />
      <KpiCard
        icon={<UserX className="h-4 w-4" />}
        label={t("pulso.inactive.label")}
        value={kpis.inactive_professors}
        delta={kpis.delta_inactive_professors}
        invertColor
      />
    </section>
  )
})
